import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  ExpandMore,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import userIcon from "../../../static/media/icon/user.svg";
import briefcaseIcon from "../../../static/media/icon/briefcase.svg";
import statisticsIcon from "../../../static/media/icon/statistics.svg";
import { AppContext } from "../../../context/AppContext";

const TopBar = ({ open, setOpen }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const { user, logoutUser, accessToken, refreshToken } = useContext(AppContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [empresa, setEmpresa] = useState("");
  const [ambiente, setAmbiente] = useState("2");

  const navigate = useNavigate();

  const email = user.email;

  const role = user.isSuperuser
    ? "SUPERUSUARIO"
    : user.isAdmin
    ? "ADMINISTRADOR"
    : user.isVendedor
    ? "VENDEDOR"
    : "";

  const isAdmin = user.isSuperuser || user.isAdmin;

  useEffect(() => {
    const fetchSistema = async () => {
      try {
        const res = await fetch("/facturas/empresa/info/", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!res.ok) return;

        const data = await res.json();

        const nc = String(data?.nombre_comercial || "").trim();
        const rs = String(data?.razon_social || "").trim();
        const em = String(data?.empresa || "").trim();

        setEmpresa(nc || rs || em || "");

        setAmbiente(String(data?.ambiente || "2").trim() === "1" ? "1" : "2");
      } catch {}
    };

    if (accessToken) fetchSistema();
  }, [accessToken]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: refreshToken,
        }),
      });
    } catch {}

    logoutUser();

    navigate("/");
  };

  const dotColor = ambiente === "2" ? "#4CAF50" : "#F44336";

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 0.8, sm: 2 },
        py: { xs: 0.65, sm: 1 },
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.4, sm: 1 },
        background: "#fafafa",
        borderBottom: "1px solid #e5e7eb",
        minHeight: { xs: 62, sm: 72 },
        position: "sticky",
        top: 0,
        zIndex: 900,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.15, sm: 0.4 },
          minWidth: 0,
        }}
      >
        <Tooltip title={open ? "Cerrar" : "Abrir"} arrow>
          <IconButton
            onClick={() => setOpen(!open)}
            size="small"
            sx={{
              width: { xs: 34, sm: 38 },
              height: { xs: 34, sm: 38 },
            }}
          >
            {isMobile ? (
              <MenuIcon sx={{ fontSize: 27, color: "black" }} />
            ) : open ? (
              <ChevronLeft sx={{ fontSize: 30, color: "black" }} />
            ) : (
              <ChevronRight sx={{ fontSize: 30, color: "black" }} />
            )}
          </IconButton>
        </Tooltip>

        <Box
          onClick={() => navigate("/admin-panel")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.35, sm: 0.65 },
            cursor: "pointer",
            px: { xs: 0.3, sm: 0.7 },
            py: 0.35,
            borderRadius: 2,
            minWidth: 0,
            "&:hover": {
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          <Dashboard
            sx={{
              fontSize: { xs: 25, sm: 28 },
              color: "black",
              flexShrink: 0,
            }}
          />

          {!isMobile && (
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 700,
                color: "#444",
                whiteSpace: "nowrap",
              }}
            >
              Panel de Control
            </Typography>
          )}
        </Box>
      </Box>

      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            px: 2,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#1565C0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {empresa || "—"}
          </Typography>
        </Box>
      )}

      {isMobile && (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#1565C0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              textAlign: "center",
            }}
          >
            {empresa || "Panel"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.25, sm: 1 },
          minWidth: 0,
        }}
      >
        {isAdmin && (
          <Tooltip title="Estadísticas" arrow>
            <Box
              onClick={() => navigate("/admin-panel/estadisticas")}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                px: { xs: 0.35, sm: 0.6 },
                py: 0.45,
                borderRadius: "12px",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              <img
                src={statisticsIcon}
                alt="Estadísticas"
                style={{
                  width: isMobile ? 25 : 28,
                  height: isMobile ? 25 : 28,
                }}
              />
            </Box>
          </Tooltip>
        )}

        {isAdmin && (
          <Tooltip title="Mi Compañía" arrow>
            <Box
              onClick={() => navigate("/admin-panel/empresa")}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                px: { xs: 0.35, sm: 0.6 },
                py: 0.45,
                borderRadius: "12px",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              <img
                src={briefcaseIcon}
                alt="Compañía"
                style={{
                  width: isMobile ? 25 : 28,
                  height: isMobile ? 25 : 28,
                }}
              />
            </Box>
          </Tooltip>
        )}

        <Box
          onClick={handleMenuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.35, sm: 0.8 },
            cursor: "pointer",
            px: { xs: 0.35, sm: 1 },
            py: 0.45,
            borderRadius: "12px",
            minWidth: 0,
            maxWidth: { xs: 112, sm: 310 },
            "&:hover": {
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <img
              src={userIcon}
              alt="Usuario"
              style={{
                width: isMobile ? 29 : 32,
                height: isMobile ? 29 : 32,
                borderRadius: "50%",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotColor,
                border: "2px solid white",
              }}
            />
          </Box>

          {!isMobile && (
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  color: "#1565C0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 220,
                  lineHeight: 1.1,
                }}
              >
                {email || "—"}
              </Typography>

              {role && (
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 220,
                    lineHeight: 1.1,
                    mt: 0.2,
                  }}
                >
                  {role}
                </Typography>
              )}
            </Box>
          )}

          <ExpandMore
            sx={{
              color: "#1565C0",
              fontSize: { xs: 20, sm: 24 },
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 210,
          },
        }}
      >
        {isMobile && (
          <Box sx={{ px: 2, py: 1.2, maxWidth: 260 }}>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 700,
                color: "#1565C0",
                wordBreak: "break-word",
                lineHeight: 1.25,
              }}
            >
              {email || "—"}
            </Typography>

            {role && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "#6b7280",
                  mt: 0.25,
                }}
              >
                {role}
              </Typography>
            )}
          </Box>
        )}

        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/admin-panel/perfil");
          }}
        >
          Mi perfil
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
        >
          Cerrar Sesión
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopBar;